import React, { useRef, useEffect, useCallback } from 'react';
import { sanitizeTcHtml } from '../../utils/sanitizeTcHtml';

/**
 * RichTextEditor — reusable WYSIWYG editor (§5.3). Never exposes raw HTML to
 * the user. Edits a contentEditable surface and emits clean HTML via onChange.
 * Used by T&C templates and any other rich-text need (email signature, etc.).
 *
 * props:
 *   value     HTML string
 *   onChange  (html) => void
 *   placeholder
 *   minHeight (px)
 */
export default function RichTextEditor({ value = '', onChange, placeholder = '', minHeight = 220 }) {
  const ref = useRef(null);
  // Tracks HTML we last emitted so a re-render from our own onChange does NOT
  // reset innerHTML (which collapses the caret to the start → reversed typing).
  // IDI-624: starts null so the first mount seeds the DOM (cleaned) too.
  const lastEmitted = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Sync the DOM only when the external value differs from what we last emitted
  // (i.e. a genuine external change), never on our own keystroke round-trips.
  // IDI-624 (D1/D2): stored HTML is cleaned to the T&C allowlist before it
  // reaches the DOM; if cleaning changed it, the cleaned HTML is emitted so
  // the parent form (and its Save) holds exactly what is shown.
  useEffect(() => {
    if (!ref.current) return;
    if (value === lastEmitted.current) return;
    const clean = sanitizeTcHtml(value);
    if (ref.current.innerHTML !== clean) {
      ref.current.innerHTML = clean; // D6 guard: `clean` is a const from sanitizeTcHtml()
    }
    lastEmitted.current = clean;
    if (clean !== (value || '') && onChangeRef.current) onChangeRef.current(clean);
  }, [value]);

  const emit = useCallback(() => {
    if (ref.current && onChange) {
      lastEmitted.current = ref.current.innerHTML;
      onChange(ref.current.innerHTML);
    }
  }, [onChange]);

  const exec = (command, arg) => {
    // execCommand is deprecated but universally supported for simple WYSIWYG
    // and avoids adding an editor dependency. Iter18 R2 Round 3 (C):
    //   • Toggle heading: if the caret is already inside an <h3>, revert to
    //     paragraph so the H button is a toggle, not a one-way action.
    //   • Wrap the tag in angle brackets — Firefox requires `<H3>` for
    //     formatBlock; Chrome/Safari accept either but the bracketed form is
    //     the safest cross-browser input.
    //   • Focus only if the editor is not already the active element —
    //     otherwise we snap the caret to the container root.
    if (command === 'formatBlock' && arg === 'H3') {
      const sel = window.getSelection && window.getSelection();
      const node = sel && sel.anchorNode;
      const inHeading = node && (function() {
        let el = node.nodeType === 1 ? node : node.parentElement;
        while (el && el !== ref.current) {
          if (el.tagName === 'H3') return true;
          el = el.parentElement;
        }
        return false;
      })();
      document.execCommand('formatBlock', false, inHeading ? '<p>' : '<h3>');
    } else {
      document.execCommand(command, false, arg);
    }
    if (ref.current && document.activeElement !== ref.current) ref.current.focus();
    emit();
  };

  const Btn = ({ cmd, arg, label, title, style }) => (
    <button
      type="button"
      title={title || label}
      onMouseDown={(e) => { e.preventDefault(); exec(cmd, arg); }}
      className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded"
      style={style}
    >
      {label}
    </button>
  );

  const isEmpty = !value || value === '<br>' || value.replace(/<[^>]+>/g, '').trim() === '';

  return (
    <div className="border border-[color:var(--color-border)] overflow-hidden" style={{ borderRadius: 'var(--radius-md)' }}>
      <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-[color:var(--color-border)]">
        <Btn cmd="bold" label="B" title="Bold" style={{ fontWeight: 700 }} />
        <Btn cmd="italic" label="I" title="Italic" style={{ fontStyle: 'italic' }} />
        <Btn cmd="underline" label="U" title="Underline" style={{ textDecoration: 'underline' }} />
        <span className="w-px h-4 bg-gray-300 mx-1" />
        <Btn cmd="insertUnorderedList" label="• List" title="Bulleted list" />
        <Btn cmd="insertOrderedList" label="1. List" title="Numbered list" />
        <span className="w-px h-4 bg-gray-300 mx-1" />
        <Btn cmd="formatBlock" arg="H3" label="H" title="Heading" style={{ fontWeight: 700 }} />
      </div>
      <div className="relative">
        {isEmpty && placeholder && (
          <span className="pointer-events-none absolute left-3 top-2 text-sm text-gray-400">{placeholder}</span>
        )}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
          role="textbox"
          aria-multiline="true"
          className="rte-content w-full px-3 py-2 text-sm focus:outline-none prose prose-sm max-w-none"
          style={{ minHeight }}
        />
        {/* Iter18 R2 Round 3 (C): ensure list markers + heading styling render
            inside the contenteditable region regardless of the surrounding
            prose class configuration. Scoped to .rte-content so it does not
            leak into the wider app. */}
        <style>{`
          .rte-content ul { list-style: disc outside; padding-left: 1.5rem; margin: 0.25rem 0; }
          .rte-content ol { list-style: decimal outside; padding-left: 1.75rem; margin: 0.25rem 0; }
          .rte-content ul li, .rte-content ol li { margin: 0.15rem 0; }
          .rte-content h3 { font-size: 1rem; font-weight: 700; margin: 0.5rem 0 0.25rem; }
          .rte-content p { margin: 0.25rem 0; }
        `}</style>
      </div>
    </div>
  );
}
